import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { catalogApi } from '@/lib/api';
import { ProductCard, ProductCardSkeleton } from '@/components/ProductCard';
import { Seo } from '@/components/Seo';
import { useLocale } from '@/hooks/useLocalePath';
import type { JSX } from 'react';

export function SearchPage(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['search', query, locale, params.get('strona')],
    queryFn: () =>
      catalogApi.list({ locale, q: query, page: Number(params.get('strona') ?? 1), pageSize: 24, sort: 'relevance' }),
    enabled: query.length > 0,
  });

  return (
    <>
      <Seo title={`${t('search.title')}: ${query}`} path="/szukaj" noindex />
      <div className="rz-shell py-10">
        <p className="rz-eyebrow mb-3">{t('search.title')}</p>
        <h1 className="text-xl md:text-2xl">
          {query ? (
            <>
              {t('search.for')} <span className="text-[var(--color-brand)]">„{query}”</span>
            </>
          ) : (
            t('common.search')
          )}
        </h1>
        <form
          className="mt-6 flex max-w-lg gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const value = new FormData(e.currentTarget).get('q');
            setParams(value ? { q: String(value) } : {});
          }}
        >
          <label htmlFor="search-input" className="sr-only">{t('common.search')}</label>
          <input id="search-input" name="q" defaultValue={query} className="rz-field" placeholder={t('common.searchPlaceholder')} />
          <button type="submit" className="rz-button shrink-0">{t('common.search')}</button>
        </form>
        {data && !data.meta.queryTooShort && (
          <p className="mt-4 text-xs text-[var(--color-muted)]" data-testid="result-count">
            {/* A capped total is a floor, not a count. */}
            {data.meta.capped
              ? t('search.cappedCount', { count: data.total })
              : t('catalog.resultsCount', { count: data.total })}
          </p>
        )}
      </div>

      <div className="rz-shell pb-24">
        {isLoading && query ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => <ProductCardSkeleton key={i} />)}
          </div>
        ) : data?.meta.queryTooShort ? (
          <div className="py-16 text-center space-y-2">
            <p className="font-display text-lg">{t('search.tooShortTitle')}</p>
            <p className="text-sm text-[var(--color-muted)]">
              {t('search.tooShortBody', { count: data.meta.minQueryLength })}
            </p>
          </div>
        ) : query && (data?.total ?? 0) === 0 ? (
          <div className="py-16 text-center space-y-2">
            <p className="font-display text-lg">{t('search.noResultsTitle', { query })}</p>
            <p className="text-sm text-[var(--color-muted)]">{t('search.noResultsBody')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4" data-testid="product-grid">
            {(data?.items ?? []).map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
